type UnitConversionFactors = {
  [key: string]: { [unit: string]: number };
};

export class UnitConverterHelper {
  private readonly conversionFactors: UnitConversionFactors;

  constructor() {
    this.conversionFactors = {
      fraction: { "%": 100 },
      volume: { m3: 1.0, MG: 264.172, gal: 264.172, ft3: 35.3147 },
      flow: {
        "m3/d": 1.0,
        "m3/h": 24.0,
        "m3/min": 1440.0,
        "m3/s": 86400.0,
        MLD: 0.001,
        "L/d": 1000.0,
        "L/h": 24000,
        "L/min": 1440000,
        "L/s": 86400000,
        MGD: 0.000264,
        gpd: 264.1721,
        gph: 6340.129257,
        gpm: 380407.7554,
        gps: 22824465.3237,
        cfd: 35.3147,
        cfh: 847.552,
        cfm: 50853.1201,
        cfs: 3051187.2049,
      },
      mass: {
        kg: 1.0,
        g: 1000.0,
        mg: 1000000.0,
        tonne: 0.001,
        lbs: 2.2046,
        ton: 0.001102,
      },
      area: { meters2: 1.0, cm2: 10000.0, feet2: 10.7639, yards2: 1.19599, inches2: 1550.0031, miles2: 3.861e-7, kilometers2: 1e-6, acres2: 0.000247105, hectares2: 1e-4 },
      length: {
        meters: 1.0,
        kilometers: 0.001,
        cm: 100,
        mm: 1000,
        feet: 3.2808,
        inches: 39.3701,
        yards: 1.09361,
        miles: 0.000621371
      },
      time: { d: 1.0, h: 24.0, min: 1440.0, s: 86400.0 },
      pressure: {
        bar: 1.0,
        mmHg: 750.0616851,
        atm: 0.986923,
        MPa: 0.1,
        Pa: 100000.0,
        psi: 14.5037738,
      },
      velocity: {
        "m/s": 1.0,
        "m/min": 60.0,
        "cm/s": 100.0,
        "cm/min": 6000.0,
        "ft/s": 3.28084,
        "ft/min": 196.8504,
        "in/s": 39.3701,
        "in/min": 2362.20472,
      },
      density: {
        "kg/m3": 1.0,
        "g/m3": 1000.0,
        "kg/L": 0.001,
        "g/L": 1.0,
        "lb/gal": 8.345404,
        "lb/cf": 62.427961,
      },
      viscosity: { "mPa-s": 1.0, cP: 1.0, "Pa-s": 0.001, "N-s/m2": 0.001 },
      pH: { "S.U.": 1.0 },
      concentrationCl2: { "mg Cl2/L": 1.0, "g Cl2/m3": 1.0 },
      concentration: { "mg/L": 1.0, "g/m3": 1.0 },
      temperature: { "˚C": 1.0, "˚F": 33.8 },
      electricEnergy: { kWh: 1.0, MWh: 0.001, GWh: 0.000001, Wh: 1000.0 },
    };
  }

  /**
   * Validates if the input value is a valid number
   */
  private validateValue(value: number): void {
    if (typeof value !== "number" || isNaN(value)) {
      throw new Error("Invalid value: Must be a valid number");
    }
  }

  /**
   * Validates if the units exist in the conversion factors
   */
  private validateUnits(fromUnit: string, toUnit: string): string {
    let unitType: string | null = null;

    for (const key in this.conversionFactors) {
      if (
        this.conversionFactors[key][fromUnit] !== undefined &&
        this.conversionFactors[key][toUnit] !== undefined
      ) {
        unitType = key;
        break;
      }
    }

    if (unitType === null) {
      throw new Error(
        `Invalid units: Cannot convert from '${fromUnit}' to '${toUnit}'`
      );
    }

    return unitType;
  }

  /**
   * Converts a value from one unit to another
   */
  public convert(value: number, fromUnit: string, toUnit: string): number {
    // Validate inputs
    this.validateValue(value);
    const unitType = this.validateUnits(fromUnit, toUnit);

    // Perform conversion
    const baseValue = value / this.conversionFactors[unitType][fromUnit];
    const convertedValue = baseValue * this.conversionFactors[unitType][toUnit];

    return convertedValue;
  }

  /**
   * Gets all available units for a specific measurement type
   */
  public getAvailableUnits(measurementType: string): string[] {
    if (!this.conversionFactors[measurementType]) {
      throw new Error(`Invalid measurement type: ${measurementType}`);
    }
    return Object.keys(this.conversionFactors[measurementType]);
  }

  /**
   * Gets all available measurement types
   */
  public getMeasurementTypes(): string[] {
    return Object.keys(this.conversionFactors);
  }
}
